import { Map } from 'immutable';
import { connect } from 'react-redux';
import { ThunkDispatch } from "redux-thunk";

import { NoteColor, ProcessStep } from "../constants";

import {
    setPaneSize,
    setScroll,
    showHelpContent,
    showTimeDialog,
    showUpdateNoteDialog,
    startNoteDraggingOnStoryboard,
    noteDraggingOnStoryboard,
    stopNoteDraggingOnStoryboard,
    deleteNote,
    slideDragging,
    stopSlideDragging,
    deleteSlide,
    Action,
    stopShapeSketching,
    shapeSketching,
    selectShape,
    shapeDragging,
    stopShapeDragging,
    selectorDragging,
    stopSelectorDragging,
    setZoomLevel,
    zoomInSlide, startPanning, panning, stopPanning,
} from '../actions';

import {
    getCurrentTemplate,
    getNoteDragging,
    getSlideDragging,
    getNotes,
    getProcessStep,
    getSlides,
    getTemplateText,
    getTextFields,
    DraggingState,
    HelpPanelContentType,
    Note,
    SlideData,
    State,
    TemplateData,
    SketchMode,
    getSketchMode,
    getSketchState,
    getShapeDragging,
    getSelectorDragging,
    getPaneState,
    ShapeDraggingState,
    SelectorDraggingState, getZoomLevel, getPanning,
} from '../reducers';

import StoryboardView from '../components/StoryboardView';
import { toStoryboardX, toStoryboardY } from "../services/template-service";
import { PanningState } from "../reducers/ui-state";


interface StateProps {
    readonly processStep: ProcessStep,
    readonly width: number,
    readonly height: number,
    readonly notes: ReadonlyArray<Note>,
    readonly slides: ReadonlyArray<SlideData>,
    readonly zoomLevel: number,
    readonly scrollLeft: number,
    readonly scrollTop: number,
    readonly panning: PanningState | null,
    readonly noteDragging: DraggingState | null,
    readonly shapeDragging: ShapeDraggingState | null,
    readonly selectorDragging: SelectorDraggingState | null,
    readonly slideDragging: DraggingState | null,
    readonly sketchMode: SketchMode,
    readonly sketchState: DraggingState | null,
    readonly templateData: TemplateData,
    readonly textFields: Map<string, string>,
    readonly templateTextSupplier: (id: string) => string,
}

interface DispatchProps {
    readonly onStartPanning: (mouseX: number, mouseY: number) => void,
    readonly onPanning: (mouseX: number, mouseY: number) => void,
    readonly onStopPanning: (mouseX: number, mouseY: number) => void,
    readonly onNoteStartDragging: (uuid: string, x: number, y: number) => void,
    readonly onNoteDragging: (x: number, y: number) => void,
    readonly onNoteStopDragging: (x: number, y: number) => void,
    readonly onNoteDoubleClicked: (uuid: string, text: string, color: NoteColor) => void,
    readonly onNoteDeleteButtonClicked: (uuid: string) => void,
    readonly onShapeSketching: (x: number, y: number) => void,
    readonly onShapeStopSketching: (x: number, y: number) => void,
    readonly onShapeDragging: (x: number, y: number) => void,
    readonly onShapeStopDragging: (x: number, y: number) => void,
    readonly onSelectorDragging: (x: number, y: number) => void,
    readonly onSelectorStopDragging: (x: number, y: number) => void,
    readonly onSlideDragging: (x: number, y: number) => void,
    readonly onSlideStopDragging: (x: number, y: number) => void,
    readonly onSlideDoubleClicked: (uuid: string) => void,
    readonly onSlideDeleteButtonClicked: (uuid: string) => void,
    readonly onBackgroundDoubleClicked: () => void,
    readonly onTemplateHelpClicked: (content: string) => void,
    readonly onTextFieldClicked: (key: string, text: string) => void,
    readonly onCanvasClicked: () => void,
    readonly onScrolled: (scrollLeft: number, scrollTop: number) => void,
    readonly onStoryboardResized: (width: number, height: number) => void,
}


const mapStateToProps = (state: State): StateProps => {
    const template = getCurrentTemplate(state);
    const { scrollLeft, scrollTop } = getPaneState(state);
    const zoomLevel = getZoomLevel(state);
    return {
        processStep: getProcessStep(state),
        width: template.storyboardWidth,
        height: template.storyboardHeight,
        notes: getNotes(state).map(note => (
            {
                ...note,
                x: toStoryboardX(template, note.x),
                y: toStoryboardY(template, note.y),
            }
        )),
        slides: getSlides(state),
        zoomLevel,
        scrollLeft,
        scrollTop,
        panning: getPanning(state),
        noteDragging: getNoteDragging(state),
        shapeDragging: getShapeDragging(state),
        selectorDragging: getSelectorDragging(state),
        slideDragging: getSlideDragging(state),
        sketchMode: getSketchMode(state),
        sketchState: getSketchState(state),
        templateData: getCurrentTemplate(state),
        textFields: getTextFields(state),
        templateTextSupplier: id => getTemplateText(state, id),
    }
};

const mapDispatchToProps = (dispatch: ThunkDispatch<State, any, Action>): DispatchProps => (
    {
        onStartPanning: (mouseX: number, mouseY: number) => {
            dispatch(startPanning(mouseX, mouseY));
        },
        onPanning: (mouseX: number, mouseY: number) => {
            dispatch(panning(mouseX, mouseY));
        },
        onStopPanning: (mouseX: number, mouseY: number) => {
            dispatch(stopPanning(mouseX, mouseY));
        },
        onNoteStartDragging: (uuid: string, x: number, y: number) => {
            dispatch(startNoteDraggingOnStoryboard(uuid, x, y));
        },
        onNoteDragging: (x: number, y: number) => {
            dispatch(noteDraggingOnStoryboard(x, y));
        },
        onNoteStopDragging: (x: number, y: number) => {
            dispatch(stopNoteDraggingOnStoryboard(x, y));
        },
        onNoteDoubleClicked: (uuid: string, text: string, color: NoteColor) => {
            dispatch(showUpdateNoteDialog(uuid, text, color));
        },
        onNoteDeleteButtonClicked: (uuid: string) => {
            dispatch(deleteNote(uuid));
        },
        onShapeDragging: (x: number, y: number) => {
            dispatch(shapeDragging(x, y));
        },
        onShapeStopDragging: (x: number, y: number) => {
            dispatch(stopShapeDragging(x, y));
        },
        onSelectorDragging: (x: number, y: number) => {
            dispatch(selectorDragging(x, y));
        },
        onSelectorStopDragging: (x: number, y: number) => {
            dispatch(stopSelectorDragging(x, y));
        },
        onShapeSketching: (x: number, y: number) => {
            dispatch(shapeSketching(x, y));
        },
        onShapeStopSketching: (x: number, y: number) => {
            dispatch(stopShapeSketching(x, y));
        },
        onSlideDragging: (x: number, y: number) => {
            dispatch(slideDragging(x, y));
        },
        onSlideStopDragging: (x: number, y: number) => {
            dispatch(stopSlideDragging(x, y));
        },
        onSlideDoubleClicked: (uuid: string) => {
            dispatch(zoomInSlide(uuid));
        },
        onSlideDeleteButtonClicked: (uuid: string) => {
            dispatch(deleteSlide(uuid));
        },
        onBackgroundDoubleClicked: () => {
            dispatch(setZoomLevel(0.0));
        },
        onTemplateHelpClicked: (content: string) => {
            dispatch(showHelpContent(HelpPanelContentType.TemplateState, content));
        },
        onTextFieldClicked: (key: string, text: string) => {
            dispatch(showTimeDialog(key, text));
        },
        onCanvasClicked: () => {
            dispatch(selectShape(null));
        },
        onScrolled: (scrollLeft: number, scrollTop: number) => {
            dispatch(setScroll(scrollLeft, scrollTop));
        },
        onStoryboardResized: (width: number, height: number) => {
            dispatch(setPaneSize(width, height));
        }
    }
);


export default connect(
    mapStateToProps,
    mapDispatchToProps
)(StoryboardView);



// WEBPACK FOOTER //
// ./src/containers/Storyboard.ts